import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useM2, fmtM2 } from "@/lib/stats";
import useTrackInView from "@/lib/useTrackInView";

export default function Gallery() {
  const [refs, setRefs] = useState([]);
  const m2 = fmtM2(useM2());
  const viewRef = useTrackInView("view_references", "referencie");

  useEffect(() => {
    api.get("/references").then((r) => setRefs(r.data)).catch(() => {});
  }, []);

  return (
    <section ref={viewRef} id="referencie" className="bg-ink-2 py-24 md:py-36 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div className="max-w-2xl">
            <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Naše referencie</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9] text-white">
              Viac ako <span className="text-gold">{m2} m²</span><br />zateplených fasád
            </h2>
          </div>
          <p className="text-white/50 max-w-sm text-base leading-relaxed">
            Rodinné a bytové domy po celom Slovensku. Kvalitná realizácia cez overených partnerov.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {refs.map((r, i) => (
            <motion.article
              key={r.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              data-testid={`reference-card-${i}`}
              className="group border border-white/10 bg-ink overflow-hidden"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={r.image}
                  alt={r.title}
                  className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 bg-gold text-ink text-xs font-bold uppercase tracking-wider px-3 py-1">
                  {r.type}
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-white/50 text-sm mb-2">
                  <MapPin className="w-4 h-4 text-gold" /> {r.location} · {r.year}
                </div>
                <h3 className="font-display text-2xl text-white leading-none mb-2">{r.title}</h3>
                {r.description && (
                  <p className="text-white/55 text-sm leading-relaxed mb-3">{r.description}</p>
                )}
                <div className="flex items-center justify-between font-mono2 text-xs text-white/50 uppercase tracking-wider border-t border-white/10 pt-3 mt-3">
                  <span>{r.material}{r.system ? ` · ${r.system}` : ""}</span>
                  <span className="text-gold">{r.area} m²</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
